import { createSelector } from 'reselect';

import { createParameterSelector } from '@pressreader/appstore';
import type { IDialogPopup } from '@pressreader/popups-types';
import { isBlockingPopup, isDialogPopup, isFloatingPopup } from '@pressreader/ui-popups';

import { MODULE_NAME } from '../constants';

import type { IState } from './reducer';

const getState = (state: { [MODULE_NAME]: IState }) => state[MODULE_NAME];

const selectPopups = createSelector(getState, state => state.popups);

/**
 * Popups that suppress the legacy popup container while they are open: every blocking popup that does not allow
 * context menus. Floating popups are non-modal and never suppress anything.
 */
const selectPopupsWithoutContextMenuSupport = createSelector(getState, state =>
    state.popups.filter(popup => isBlockingPopup(popup) && !(popup as IDialogPopup).allowContextMenu),
);

const selectId = createParameterSelector<'id', string>('id');

const selectPopupById = createSelector(getState, selectId, (state, id) => state.popups.find(popup => popup.id === id));

const selectDialogPopups = createSelector(selectPopups, popups => popups.filter(isDialogPopup));

/**
 * The dialog popup on top of the popup stack — the one the popup container (`PopupContainerComponent` in
 * ui-popups) renders as the active dialog. `undefined` when no dialog is open.
 */
const selectTopDialogPopup = createSelector(selectDialogPopups, (dialogs): IDialogPopup | undefined => dialogs[dialogs.length - 1]);

const selectHasFloatingPopups = createSelector(selectPopups, popups => popups.some(isFloatingPopup));

/**
 * Whether a blocking popup — dialog, menu or alert — is open. Floating popups never count: they are non-modal, so
 * everything keyed on "a popup is open" (the overlay root class, the modal flag, reading analytics, tours) must
 * ignore them.
 */
const selectPopupOpened = createSelector(getState, state => state.popups.some(isBlockingPopup));

const selectBaselinePopups = createSelector(getState, state => state.baselinePopups);

const selectIsAnyModalOpened = createSelector(
    selectPopupOpened,
    selectBaselinePopups,
    (popupOpened, baselinePopups) => popupOpened || baselinePopups.length > 0,
);

export {
    selectBaselinePopups,
    selectDialogPopups,
    selectHasFloatingPopups,
    selectIsAnyModalOpened,
    selectPopupById,
    selectPopupOpened,
    selectPopups,
    selectPopupsWithoutContextMenuSupport,
    selectTopDialogPopup,
};
